import * as React from 'react';
import { yupResolver } from '@hookform/resolvers/yup';
import { FormProvider, SubmitHandler, useForm, useWatch } from 'react-hook-form';

import { CoreProps } from 'common/types';
import { getMetadata, parseBlogContent } from 'common/utils';
import {
  BlogFields,
  fields,
  CLEAR_FORM_VALUES,
  validationSchema,
} from 'modules/blogs/consts';
import BlogPickerForm from './BlogPickerForm';
import { useSave } from '../hooks';
import { toastMessages } from 'common/toastMessages';
import { useToast } from '../../toast';

const BlogCoreWithGet = ({ pk, defaultValues, layout }: CoreProps<BlogFields>) => {
  const { toast } = useToast();
  const { save, isSaving, error, created } = useSave(pk);

  const [blogHtml, setBlogHtml] = React.useState('');

  const form = useForm<BlogFields>({
    defaultValues: layout === 'creation' ? CLEAR_FORM_VALUES : undefined,
    resolver: yupResolver(validationSchema),
  });

  const { setValue, control, handleSubmit, reset: formReset } = form;

  React.useLayoutEffect(() => {
    if (pk) {
      formReset({ ...CLEAR_FORM_VALUES, ...defaultValues });
    }
  }, [pk]);

  const blogUrl = useWatch<BlogFields['url']>({ control, name: fields.url.name });
  const imageUrl = useWatch<BlogFields['image_url']>({
    control,
    name: fields.image_url.name,
  });

  React.useEffect(() => {
    if (defaultValues) {
      getMetadata(defaultValues.url || '').then((res) => {
        const blogContent = res?.content && parseBlogContent(res.content);
        setBlogHtml(blogContent || '');
      });
    }
  }, [defaultValues, setValue]);

  const handleCheckClick = () => {
    if (blogUrl) {
      getMetadata(blogUrl)
        .then((res) => {
          const blogContent = res?.content && parseBlogContent(res.content);
          setBlogHtml(blogContent || '');
          setValue(fields.title.name, res.title);
          setValue(fields.description.name, res.description);
          setValue(fields.image_url.name, res.image_url);
        })
        .catch(() => {
          toast(toastMessages.ERROR.GENERIC_ERROR, 'error');
        });
    }
  };

  const disabled = !blogUrl && !defaultValues;

  const handleSave: SubmitHandler<BlogFields> = (values) => {
    save(values);
  };

  return (
    <FormProvider {...form}>
      {defaultValues && (
        <BlogPickerForm
          key={pk}
          onSubmit={handleSubmit(handleSave)}
          handleCheckClick={handleCheckClick}
          isSubmitting={isSaving}
          imageUrl={imageUrl}
          defaultValues={{ ...CLEAR_FORM_VALUES, ...defaultValues }}
          disabled={disabled}
          blogHtml={blogHtml}
          error={error}
          pk={pk}
          layout={layout}
          created={created}
        />
      )}
    </FormProvider>
  );
};

export default BlogCoreWithGet;
